import {Types} from 'mongoose';
import {
    AbsenceEarlyWarningMailSkipReason,
    AbsenceEarlyWarningMailStatus,
    AbsenceEarlyWarningTriggerMode,
} from '@/enums/workflow-manager/AbsenceEarlyWarning.enum';

/**
 * Optional structured origin of a to-do (shrd ToDoItemSource), set by services such as the early warning.
 * Readable together with the description. To-dos without it show their description as before.
 */
export enum ToDoItemSourceTypes {
    ABSENCE_EARLY_WARNING = 'absenceEarlyWarning',
    /** "Verschobene Stunde …: Lehrkraft klären" (course-manager, moved lesson crossing a teacher change) */
    MOVED_LESSON_TEACHER = 'movedLessonTeacher',
    /** QM-Cockpit case (workflow-manager quality-cases) */
    QUALITY_CASE = 'qualityCase',
    /** Substitution inquiry (sick report, all declined, withdrawn): the to-do opens the inquiry */
    SUBSTITUTION_INQUIRY = 'substitutionInquiry',
}

export type AbsenceEarlyWarningToDoSource = {
    type: `${ToDoItemSourceTypes.ABSENCE_EARLY_WARNING}`;
    record?: Types.ObjectId | string;
    workflow?: Types.ObjectId | string;
    ruleName?: string;
    summary?: string;
    triggerMode?: AbsenceEarlyWarningTriggerMode;
    threshold?: number;
    window?: number;
    countExcused?: boolean;
    /** Latest evaluated lessons, newest first (0 anwesend, 1 entschuldigt, 2 unentschuldigt) */
    lessons?: Array<{ state: number; instance: Date | string }>;
    course?: {
        customId?: string; course?: Types.ObjectId | string; subject?: string; dayOfWeek?: number; startTime?: number; endTime?: number;
        institution?: Types.ObjectId | string; institutionName?: string; city?: string; state?: string;
    };
    mail?: {
        status?: AbsenceEarlyWarningMailStatus; reason?: AbsenceEarlyWarningMailSkipReason;
        recipientType?: 'parent' | 'student'; recipient?: string; recipientName?: string;
    };
    /** The description was edited by hand: show it in addition */
    descriptionEdited?: boolean;
};

/** Teacher decision of a moved lesson that crosses a teacher change (opened from the to-do: MovedLessonTeacherDialog) */
export type MovedLessonTeacherToDoSource = {
    type: `${ToDoItemSourceTypes.MOVED_LESSON_TEACHER}`;
    lessonMove: Types.ObjectId | string;
    courseCustomId: string;
    /** Course segment of the original lesson */
    course?: Types.ObjectId | string;
    originalDate: Date | string;
    newStart: Date | string;
    newEnd?: Date | string;
    originalTeacherName?: string;
    newTeacherName?: string;
    /** The description was edited by hand: show it in addition */
    descriptionEdited?: boolean;
};

/** Case of the QM-Cockpit (workflow-manager quality-cases); the to-do opens the case in the cockpit */
export type QualityCaseToDoSource = {
    type: `${ToDoItemSourceTypes.QUALITY_CASE}`;
    caseId: Types.ObjectId | string;
    /** QualityCaseType */
    caseType: string;
    /** Short summary, e.g. "Kündigung zum 31.10.2026 – Gitarre KS012" */
    summary?: string;
    courseCustomId?: string;
    state?: string;
    /** The description was edited by hand: show it in addition */
    descriptionEdited?: boolean;
};

/** Copy of shrd SubstitutionInquiryToDoSource */
export type SubstitutionInquiryToDoSource = {
    type: `${ToDoItemSourceTypes.SUBSTITUTION_INQUIRY}`;
    inquiry?: string;
    inquiries?: string[];
    sickReport?: string;
    day?: Date | string;
    /** "Nachbereitung offen" of a past inquiry (daily check of the substitution-manager) */
    followUp?: boolean;
    descriptionEdited?: boolean;
};

export type ToDoItemSource = AbsenceEarlyWarningToDoSource | MovedLessonTeacherToDoSource | QualityCaseToDoSource | SubstitutionInquiryToDoSource;
